// @ts-nocheck
import { useState, useCallback } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Tabs,
  Group,
  Button,
  Select,
  Text,
  Paper,
  Progress,
  Alert,
  Anchor,
  List,
  Card,
  FileInput,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconUpload,
  IconDownload,
  IconFileSpreadsheet,
  IconAlertCircle,
  IconCheck,
  IconFileImport,
  IconFileExport,
} from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { EmptyState } from '../../components/EmptyState';
import {
  useImportJobs,
  useStartImport,
  useExportData,
  useExportJobs,
  type ImportJob,
  type ExportJob,
} from '../../hooks/useImportExport';

const ENTITY_TYPE_OPTIONS = [
  { value: 'employees', label: 'Employees' },
  { value: 'timesheets', label: 'Timesheets' },
  { value: 'schedules', label: 'Schedules' },
  { value: 'amendments', label: 'Amendments' },
  { value: 'pay_rates', label: 'Pay Rates' },
];

const EXPORT_FORMAT_OPTIONS = [
  { value: 'csv', label: 'CSV' },
  { value: 'xlsx', label: 'Excel (XLSX)' },
  { value: 'pdf', label: 'PDF' },
];

function formatTimestamp(ts: string | null) {
  if (!ts) return '--';
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatEntityType(val: string) {
  return val
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Import Tab
// ---------------------------------------------------------------------------

function ImportTab() {
  const [importPage, setImportPage] = useState(1);
  const [entityType, setEntityType] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const { data: importJobsRaw, loading: isLoading } = useImportJobs({ page: importPage, perPage: 25 });
  const importJobsList = (importJobsRaw ?? []) as unknown as ImportJob[];
  const startImport = useStartImport();

  const handleImport = useCallback(async () => {
    if (!entityType) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select an entity type.',
        color: 'red',
      });
      return;
    }
    if (!file) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select a CSV file to import.',
        color: 'red',
      });
      return;
    }

    try {
      const fileContent = await file.text();
      await startImport.mutateAsync({
        entityType,
        fileContent,
        fileName: file.name,
      });
      notifications.show({
        title: 'Import Started',
        message: `Import of ${file.name} has been queued for processing.`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      setFile(null);
      setEntityType(null);
    } catch (err) {
      notifications.show({
        title: 'Import Failed',
        message: err instanceof Error ? err.message : 'An unexpected error occurred.',
        color: 'red',
      });
    }
  }, [entityType, file, startImport]);

  const importColumns: Column<ImportJob>[] = [
    {
      key: 'fileName',
      label: 'File Name',
      sortable: true,
    },
    {
      key: 'entityType',
      label: 'Entity Type',
      sortable: true,
      render: (row) => formatEntityType(row.entityType),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 120,
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'totalRows',
      label: 'Total Rows',
      width: 100,
      render: (row) => row.totalRows.toLocaleString(),
    },
    {
      key: 'processedRows',
      label: 'Processed',
      width: 100,
      render: (row) => row.processedRows.toLocaleString(),
    },
    {
      key: 'errorRows',
      label: 'Errors',
      width: 80,
      render: (row) => (
        <Text c={row.errorRows > 0 ? 'red' : undefined} size="sm">
          {row.errorRows.toLocaleString()}
        </Text>
      ),
    },
    {
      key: 'createdAt',
      label: 'Started',
      sortable: true,
      width: 180,
      render: (row) => formatTimestamp(row.createdAt),
    },
  ];

  return (
    <Stack gap="lg">
      {/* Upload section */}
      <Paper p="lg" withBorder>
        <Stack gap="md">
          <Group gap="xs">
            <IconFileImport size={20} />
            <Text fw={600} size="lg">
              Upload CSV File
            </Text>
          </Group>

          <Text size="sm" c="dimmed">
            Select an entity type and upload a CSV file to import data. The first
            row must contain column headers matching the expected schema.
          </Text>

          <Group ta="end" gap="md">
            <Select
              label="Entity Type"
              placeholder="Select entity type"
              data={ENTITY_TYPE_OPTIONS}
              value={entityType}
              onChange={setEntityType}
              w={220}
              required
            />
            <FileInput
              label="CSV File"
              placeholder="Choose file..."
              accept=".csv,text/csv"
              value={file}
              onChange={setFile}
              icon={<IconFileSpreadsheet size={16} />}
              w={300}
              required
            />
            <Button
              icon={<IconUpload size={16} />}
              onClick={handleImport}
              loading={startImport.isPending}
              disabled={!entityType || !file}
            >
              Start Import
            </Button>
          </Group>
        </Stack>
      </Paper>

      {/* In-progress imports */}
      {importJobsList?.some((j: ImportJob) => j.status === 'processing') && (
        <Stack gap="sm">
          {importJobsList
            .filter((j: ImportJob) => j.status === 'processing')
            .map((job) => {
              const pct =
                job.totalRows > 0
                  ? Math.round((job.processedRows / job.totalRows) * 100)
                  : 0;
              return (
                <Alert
                  key={job.id}
                  title={`Importing ${job.fileName}`}
                  icon={<IconFileImport size={16} />}
                  color="blue"
                >
                  <Stack gap="xs">
                    <Text size="sm">
                      {job.processedRows.toLocaleString()} of{' '}
                      {job.totalRows.toLocaleString()} rows processed
                      {job.errorRows > 0 &&
                        ` (${job.errorRows.toLocaleString()} errors)`}
                    </Text>
                    <Progress value={pct} size="sm" animated />
                  </Stack>
                </Alert>
              );
            })}
        </Stack>
      )}

      {/* Error details for recent failed imports */}
      {importJobsList?.some(
        (j: ImportJob) => j.status === 'failed' && j.errors.length > 0,
      ) && (
        <Stack gap="sm">
          {importJobsList
            .filter((j: ImportJob) => j.status === 'failed' && j.errors.length > 0)
            .slice(0, 3)
            .map((job) => (
              <Alert
                key={job.id}
                title={`Errors in ${job.fileName}`}
                icon={<IconAlertCircle size={16} />}
                color="red"
                variant="light"
              >
                <List size="sm" spacing={4}>
                  {job.errors.slice(0, 10).map((error, idx) => (
                    <List.Item key={idx}>{error}</List.Item>
                  ))}
                  {job.errors.length > 10 && (
                    <List.Item>
                      ...and {job.errors.length - 10} more errors
                    </List.Item>
                  )}
                </List>
              </Alert>
            ))}
        </Stack>
      )}

      {/* Import history table */}
      <Stack gap="xs">
        <Text fw={600} size="lg">
          Import History
        </Text>
        {!isLoading && (!importJobsList || importJobsList.length === 0) ? (
          <EmptyState
            title="No imports yet"
            message="Upload a CSV file above to get started with your first data import."
          />
        ) : (
          <DataTable<Record<string, unknown>>
            columns={importColumns as unknown as Column<Record<string, unknown>>[]}
            data={(importJobsList ?? []) as unknown as Record<string, unknown>[]}
            loading={isLoading}
            pagination={{
              page: importPage,
              pageSize: 25,
              total: importJobsList?.length ?? 0,
              onPageChange: setImportPage,
            }}
            getRowKey={(row) => row.id as string}
            emptyMessage="No import jobs found"
          />
        )}
      </Stack>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Export Tab
// ---------------------------------------------------------------------------

function ExportTab() {
  const [entityType, setEntityType] = useState<string | null>(null);
  const [format, setFormat] = useState<string | null>('csv');
  const [dateRange, setDateRange] = useState<{
    startDate?: string;
    endDate?: string;
  }>({});
  const [exportPage, setExportPage] = useState(1);

  const exportData = useExportData();
  const { data: exportJobsRaw, isLoading } = useExportJobs({ page: exportPage, perPage: 10 });
  const exportJobs = (exportJobsRaw as any)?.exportJobs as { data: ExportJob[]; total: number } | undefined;

  const handleExport = useCallback(async () => {
    if (!entityType) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select an entity type to export.',
        color: 'red',
      });
      return;
    }
    if (!format) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select an export format.',
        color: 'red',
      });
      return;
    }

    try {
      const filters: Record<string, unknown> = {};
      if (dateRange.startDate) filters.startDate = dateRange.startDate;
      if (dateRange.endDate) filters.endDate = dateRange.endDate;

      const result = await exportData.mutateAsync({
        entityType,
        format,
        filters: Object.keys(filters).length > 0 ? filters : undefined,
      });

      if ((result as any)?.exportData?.downloadUrl) {
        window.open((result as any).exportData.downloadUrl, '_blank');
      }

      notifications.show({
        title: 'Export Started',
        message: `Your ${formatEntityType(entityType)} export is being generated.`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch (err) {
      notifications.show({
        title: 'Export Failed',
        message: err instanceof Error ? err.message : 'An unexpected error occurred.',
        color: 'red',
      });
    }
  }, [entityType, format, dateRange, exportData]);

  const handleDateApply = useCallback(
    (startDate: string, endDate: string) => {
      setDateRange({ startDate, endDate });
    },
    [],
  );

  const exportColumns: Column<ExportJob>[] = [
    {
      key: 'entityType',
      label: 'Entity Type',
      render: (row) => formatEntityType(row.entityType),
    },
    {
      key: 'format',
      label: 'Format',
      width: 80,
      render: (row) => row.format.toUpperCase(),
    },
    {
      key: 'status',
      label: 'Status',
      width: 120,
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      width: 180,
      render: (row) => formatTimestamp(row.createdAt),
    },
    {
      key: 'downloadUrl',
      label: 'Download',
      width: 120,
      render: (row) =>
        row.downloadUrl && row.status === 'completed' ? (
          <Anchor href={row.downloadUrl} target="_blank" size="sm">
            <Group gap={4}>
              <IconDownload size={14} />
              Download
            </Group>
          </Anchor>
        ) : (
          <Text size="sm" c="dimmed">
            --
          </Text>
        ),
    },
  ];

  return (
    <Stack gap="lg">
      {/* Export controls */}
      <Paper p="lg" withBorder>
        <Stack gap="md">
          <Group gap="xs">
            <IconFileExport size={20} />
            <Text fw={600} size="lg">
              Export Data
            </Text>
          </Group>

          <Text size="sm" c="dimmed">
            Select the data type and format to generate an export file. You may
            optionally filter by date range.
          </Text>

          <Group ta="end" gap="md" wrap="wrap">
            <Select
              label="Entity Type"
              placeholder="Select entity type"
              data={ENTITY_TYPE_OPTIONS}
              value={entityType}
              onChange={setEntityType}
              w={220}
              required
            />
            <Select
              label="Format"
              placeholder="Select format"
              data={EXPORT_FORMAT_OPTIONS}
              value={format}
              onChange={setFormat}
              w={180}
              required
            />
          </Group>

          {/* Optional date range */}
          <Card withBorder p="sm" bg="gray.0">
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Date Range (optional)
              </Text>
              <Group gap="sm">
                <input
                  type="date"
                  value={dateRange.startDate ?? ''}
                  onChange={(e) =>
                    setDateRange((prev) => ({ ...prev, startDate: e.target.value }))
                  }
                  style={{
                    padding: '6px 10px',
                    borderRadius: 4,
                    border: '1px solid var(--mantine-color-gray-4)',
                    fontSize: 14,
                  }}
                />
                <Text size="sm" c="dimmed">
                  to
                </Text>
                <input
                  type="date"
                  value={dateRange.endDate ?? ''}
                  onChange={(e) =>
                    setDateRange((prev) => ({ ...prev, endDate: e.target.value }))
                  }
                  style={{
                    padding: '6px 10px',
                    borderRadius: 4,
                    border: '1px solid var(--mantine-color-gray-4)',
                    fontSize: 14,
                  }}
                />
                {(dateRange.startDate || dateRange.endDate) && (
                  <Button
                    size="xs"
                    variant="subtle"
                    color="gray"
                    onClick={() => setDateRange({})}
                  >
                    Clear
                  </Button>
                )}
              </Group>
            </Stack>
          </Card>

          <Group>
            <Button
              icon={<IconDownload size={16} />}
              onClick={handleExport}
              loading={exportData.isPending}
              disabled={!entityType || !format}
            >
              Export Data
            </Button>
          </Group>
        </Stack>
      </Paper>

      {/* Recent exports */}
      <Stack gap="xs">
        <Text fw={600} size="lg">
          Recent Exports
        </Text>
        {!isLoading && (!exportJobs?.data || exportJobs.data.length === 0) ? (
          <EmptyState
            title="No exports yet"
            message="Generate an export above and it will appear here with a download link."
          />
        ) : (
          <DataTable<Record<string, unknown>>
            columns={exportColumns as unknown as Column<Record<string, unknown>>[]}
            data={(exportJobs?.data ?? []) as unknown as Record<string, unknown>[]}
            loading={isLoading}
            pagination={{
              page: exportPage,
              pageSize: 10,
              total: exportJobs?.total ?? 0,
              onPageChange: setExportPage,
            }}
            getRowKey={(row) => row.id as string}
            emptyMessage="No export jobs found"
          />
        )}
      </Stack>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function ImportExportPage() {
  return (
    <Stack gap="md">
      <PageHeader
        title="Import / Export"
        subtitle="Bulk import data from CSV files or export your data in multiple formats"
        breadcrumbs={[
          { label: 'Settings', to: '/admin/workforce/settings' },
          { label: 'Import / Export' },
        ]}
      />

      <Tabs defaultValue="import" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="import" icon={<IconUpload size={16} />}>
            Import
          </Tabs.Tab>
          <Tabs.Tab value="export" icon={<IconDownload size={16} />}>
            Export
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="import" pt="lg">
          <ImportTab />
        </Tabs.Panel>

        <Tabs.Panel value="export" pt="lg">
          <ExportTab />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
